// src/components/admin/ImageUpload.tsx
import React, { ReactNode } from 'react';
import imageCompression from 'browser-image-compression';

// 新しい画像の型定義
export type UploadedImage =
  | { id: number; url: string } // サーバーに存在する既存画像の場合
  | { file: File; url: string }; // 新規アップロード画像の場合

interface ImageUploadProps {
  label: ReactNode;
  subLabel?: string;
  images: UploadedImage[];
  onAddImages: (files: File[]) => void;
  onRemoveImage?: (index: number) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  subLabel,
  images,
  onAddImages,
  onRemoveImage,
}) => {
  // ファイルが選択されたときの処理
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);

      // 1MB以上のファイルは圧縮する
      const processedFiles = await Promise.all(
        filesArray.map(async (file) => {
          if (file.size > 1 * 1024 * 1024) {
            const options = {
              maxSizeMB: 1, // 1MB以下に圧縮
              maxWidthOrHeight: 1920, // 必要に応じて最大幅・高さを指定
              useWebWorker: true,
            };
            try {
              const compressedFile = await imageCompression(file, options);
              return compressedFile;
            } catch (error) {
              console.error('画像圧縮に失敗しました:', error);
              return file;
            }
          }
          return file;
        })
      );

      onAddImages(processedFiles);
    }
  };

  return (
    <div className="mb-6 border-gray-400">
      <div className="flex items-start my-3">
        {/* ラベル部分 */}
        <label className="font-bold w-32 shrink-0">{label}</label>
        {/* 右側の表示部分 */}
        <div className="flex flex-col grow min-w-0">
          <div className="flex items-start">
            {subLabel && (
              <span className="flex-shrink-0 mr-2" style={{ width: '100px' }}>
                {subLabel}
              </span>
            )}
            {/* コンテンツ部分 */}
            <div className="flex items-center grow">
              <div
                className="border border-gray-400 p-4 rounded bg-white"
                style={{ overflowX: 'auto' }}
              >
                {/* 固定サイズのグリッド：各セルは横90px固定 */}
                <div
                  className="grid gap-2"
                  style={{ gridTemplateColumns: 'repeat(6, 90px)' }}
                >
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="relative"
                      style={{ width: '90px', height: '150px' }}
                    >
                      <img
                        src={image.url}
                        alt={`登録済み写真 ${index + 1}`}
                        className="rounded-lg object-cover w-full h-full"
                      />
                      {onRemoveImage && (
                        <button
                          type="button"
                          onClick={() => onRemoveImage(index)}
                          className="absolute top-1 left-1 text-red-600 text-2xl font-bold"
                          style={{ zIndex: 10 }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {/* 画像追加ボタン */}
                  <div
                    className="flex justify-center items-center border-2 border-dashed rounded-lg relative"
                    style={{ width: '90px', height: '150px' }}
                  >
                    <span className="text-gray-500 text-3xl">+</span>
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>
              {/* ダミーのスペース */}
              <div
                className="ml-4"
                style={{ width: '60px', height: '23px' }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
